import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Package, Users, FileText, Calendar, TrendingUp, AlertTriangle, ArrowRight,
  Download, Share2, UserPlus, Award, X
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// Couleurs pour les graphiques (PieChart)
const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b'];

// Statuts permettant de définir ce qu'est le "Chiffre d'Affaires"
const REVENUE_STATUSES = [
  { value: 'accepted', label: 'Accepté' },
  { value: 'signed', label: 'Signé' },
  { value: 'invoiced', label: 'Facturé' },
  { value: 'paid', label: 'Payé' }
];

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'primary' }) => (
  <Card className="card-hover">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl md:text-3xl font-heading font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          {trend && (
            <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
              <TrendingUp className="w-3 h-3" /> {trend}
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}/10 dark:bg-${color}/20 flex items-center justify-center shrink-0`}>
          <Icon className={`w-6 h-6 text-${color} dark:text-${color}-400`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { token } = useAuthStore();
  
  // Gestion des filtres dans l'URL
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams.get('start') || '';
  const endDate = searchParams.get('end') || '';
  
  // Détection du Mode Rapport via l'URL
  const isReportView = searchParams.get('view') === 'report';
  
  // Récupère les statuts depuis l'URL (ou utilise Signé/Facturé/Payé par défaut)
  const statusesParam = searchParams.get('statuses');
  const selectedStatuses = statusesParam ? statusesParam.split(',') : ['signed', 'invoiced', 'paid'];

  const updateFilters = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  const toggleStatus = (statusValue) => {
    let newStatuses;
    if (selectedStatuses.includes(statusValue)) {
      newStatuses = selectedStatuses.filter(s => s !== statusValue);
      if (newStatuses.length === 0) return; 
    } else {
      newStatuses = [...selectedStatuses, statusValue];
    }
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('statuses', newStatuses.join(','));
    setSearchParams(newParams);
  };

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats', startDate, endDate, selectedStatuses],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      selectedStatuses.forEach(status => params.append('statuses', status));

      const res = await fetch(`${API_URL}/dashboard/stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  const handleExportPDF = () => {
    window.print(); 
  };

  const handleShareLink = () => {
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set('view', 'report'); // Force le mode rapport pour le destinataire
    
    navigator.clipboard.writeText(shareUrl.toString());
    toast.success("Lien copié ! Le destinataire verra un rapport plein écran verrouillé.");
  };

  if (isLoading) {
    return <div className="p-8 flex justify-center"><Skeleton className="h-[500px] w-full" /></div>;
  }

  // Calcul Marge
  const totalRevenue = stats?.total_revenue || 0;
  const totalExpenses = stats?.total_expenses || 0;
  const margin = totalRevenue - totalExpenses;
  const marginPercentage = totalRevenue > 0 ? ((margin / totalRevenue) * 100).toFixed(1) : 0;
  const isProfitable = margin >= 0;

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--card))',
    borderColor: 'hsl(var(--border))',
    color: 'hsl(var(--foreground))',
    borderRadius: '0.5rem',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
  };

  return (
    // Plein écran absolu si isReportView est vrai
    <div 
      className={isReportView 
        ? "fixed inset-0 z-[9999] bg-slate-50 dark:bg-slate-950 overflow-y-auto p-4 md:p-8 space-y-6" 
        : "space-y-6"} 
      data-testid="dashboard" 
      id="dashboard-content"
    >
      
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-3">
            Tableau de bord
            {isReportView && <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Rapport Financier</Badge>}
          </h1>
          <p className="text-muted-foreground">Performances et suivi de l'activité</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          
          {/* On n'affiche le bouton Partager que si on N'EST PAS en mode rapport */}
          {!isReportView && (
            <Button variant="outline" size="sm" onClick={handleShareLink} className="h-10">
              <Share2 className="w-4 h-4 mr-2" /> Partager
            </Button>
          )}

          <Button variant="outline" size="sm" onClick={handleExportPDF} className="h-10">
            <Download className="w-4 h-4 mr-2" /> Rapport PDF
          </Button>
          
          {/* On cache la cloche de notification pour le visiteur */}
          {!isReportView && <NotificationBell />}
        </div>
      </div>

      {/* --- FILTRES (Période & Statuts) --- */}
      <div className="flex flex-col lg:flex-row gap-4 print:hidden">
        
        {/* Composant Calendrier */}
        <div className="flex flex-col sm:flex-row gap-4 items-center bg-card p-2 rounded-xl border shadow-sm w-fit">
          <div className="flex items-center pl-2 text-muted-foreground">
            <Calendar className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Période :</span>
          </div>
          <div className="flex items-center gap-2">
            <Input 
              type="date" 
              value={startDate} 
              onChange={(e) => updateFilters('start', e.target.value)}
              className="h-9 border-none bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors w-auto text-sm [color-scheme:light_dark]"
            />
            <span className="text-muted-foreground text-sm">au</span>
            <Input 
              type="date" 
              value={endDate} 
              onChange={(e) => updateFilters('end', e.target.value)}
              className="h-9 border-none bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors w-auto text-sm [color-scheme:light_dark]"
            />
            {(startDate || endDate) && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { updateFilters('start', ''); updateFilters('end', ''); }}
                className="h-9 px-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Effacer les dates"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Sélecteur de Statuts pour le CA */}
        <div className="flex flex-col sm:flex-row items-center gap-3 bg-card p-2 rounded-xl border shadow-sm w-fit">
          <span className="text-sm font-medium text-muted-foreground ml-2">
            CA basé sur :
          </span>
          <div className="flex gap-1.5 flex-wrap">
            {REVENUE_STATUSES.map((status) => {
              const isActive = selectedStatuses.includes(status.value);
              return (
                <Badge
                  key={status.value}
                  variant={isActive ? "default" : "outline"}
                  className={`cursor-pointer transition-colors px-3 py-1 ${
                    isActive 
                      ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                      : "hover:bg-primary/10 dark:hover:bg-primary/20 text-muted-foreground border-slate-200 dark:border-slate-700"
                  }`}
                  onClick={() => toggleStatus(status.value)}
                >
                  {status.label}
                </Badge>
              );
            })}
          </div>
        </div>

      </div>

      {/* --- KPI FINANCIERS PRINCIPAUX --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Carte CA */}
        <Card className="bg-slate-900 dark:bg-slate-950 text-white border-none shadow-lg">
          <CardContent className="p-6">
            <p className="text-slate-400 mb-1">Chiffre d'Affaires (Généré)</p>
            <p className="text-4xl font-heading font-bold">{formatCurrency(totalRevenue)}</p>
            <p className="text-sm text-slate-400 mt-2">Basé sur vos filtres actifs</p>
          </CardContent>
        </Card>
        
        {/* Carte Marge */}
        <Card className={`relative overflow-hidden shadow-sm transition-colors
          ${isProfitable 
            ? 'bg-white border-green-200 dark:bg-slate-900 dark:border-green-900/50' 
            : 'bg-white border-red-200 dark:bg-slate-900 dark:border-red-900/50'}`}
        >
          <div className={`absolute top-0 right-0 w-32 h-32 rounded-full -mr-10 -mt-10 blur-2xl 
            ${isProfitable ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}>
          </div>
          
          <CardContent className="p-6 relative z-10">
            <p className="text-muted-foreground mb-1">Marge Nette</p>
            <div className="flex items-end gap-3">
              <p className={`text-4xl font-heading font-bold 
                ${isProfitable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
              >
                {formatCurrency(margin)}
              </p>
              <Badge variant="secondary" className={`mb-1
                ${isProfitable ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                               : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}
              >
                {marginPercentage}%
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">CA - {formatCurrency(totalExpenses)} (Dépenses)</p>
          </CardContent>
        </Card>

        {/* Autres métriques */}
        <div className="grid grid-cols-2 gap-4">
          <StatCard title="Nvx Clients" value={stats?.counts?.new_clients || 0} icon={UserPlus} color="blue" />
          <StatCard title="Prestations" value={stats?.counts?.events_completed || 0} icon={Award} color="purple" />
        </div>
      </div>

      {/* --- GRAPHIQUES --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Évolution des Revenus & Dépenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.revenue_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#888888" strokeOpacity={0.2} />
                  <XAxis dataKey="date" fontSize={12} tickLine={false} axisLine={false} tick={{fill: '#888888'}} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}€`} tick={{fill: '#888888'}} />
                  <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Line type="monotone" name="Chiffre d'affaires" dataKey="revenue" stroke="#0ea5e9" strokeWidth={3} dot={false} />
                  <Line type="monotone" name="Dépenses" dataKey="expenses" stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Services / Produits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.top_products || []} layout="vertical" margin={{ top: 0, right: 0, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#888888" strokeOpacity={0.2} />
                  <XAxis type="number" fontSize={12} hide />
                  <YAxis dataKey="name" type="category" fontSize={12} width={100} tickLine={false} axisLine={false} tick={{fill: '#888888'}} />
                  <Tooltip cursor={{fill: 'rgba(136, 136, 136, 0.1)'}} formatter={(value) => formatCurrency(value)} contentStyle={tooltipStyle} />
                  <Bar dataKey="revenue" name="CA Généré" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Répartition par Client (Top 5)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats?.revenue_by_client || []}
                    cx="50%" cy="50%" innerRadius={60} outerRadius={80}
                    paddingAngle={5} dataKey="revenue" nameKey="client_name"
                    stroke="none"
                  >
                    {(stats?.revenue_by_client || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={tooltipStyle} itemStyle={{ color: 'hsl(var(--foreground))' }} />
                  <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- STATISTIQUES GLOBALES --- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
        <StatCard title="Total Produits" value={stats?.counts?.total_products || 0} icon={Package} color="slate" />
        <StatCard title="Total Clients" value={stats?.counts?.total_clients || 0} icon={Users} color="slate" />
        <StatCard title="Total Devis" value={stats?.counts?.total_quotes || 0} icon={FileText} color="slate" />
        <StatCard title="Événements Globaux" value={stats?.counts?.total_events || 0} icon={Calendar} color="slate" />
      </div>

      {/* 🔴 On cache conditionnellement toute cette partie si "isReportView" est VRAI */}
      {!isReportView && (
        <>
          {/* Recent & Upcoming */}
          <div className="grid md:grid-cols-2 gap-6 print:hidden">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg font-heading">Derniers devis</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/quotes">
                    Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.recent_quotes?.length > 0 ? (
                  stats.recent_quotes.map((quote) => (
                    <Link
                      key={quote.id}
                      to={`/quotes/${quote.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                      data-testid={`recent-quote-${quote.id}`}
                    >
                      <div>
                        <p className="font-medium">{quote.reference}</p>
                        <p className="text-sm text-muted-foreground">{quote.client_name || 'Sans client'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(quote.total_ttc)}</p>
                        <Badge variant="outline" className={getStatusColor(quote.status)}>
                          {quote.status}
                        </Badge>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">Aucun devis récent</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg font-heading">Événements à venir</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/events">
                    Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats?.upcoming_events?.length > 0 ? (
                  stats.upcoming_events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/50"
                      data-testid={`upcoming-event-${event.id}`}
                    >
                      <div>
                        <p className="font-medium">{event.title}</p>
                        <p className="text-sm text-muted-foreground">{event.client_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{formatDate(event.start_date)}</p>
                        <p className="text-xs text-muted-foreground">{event.location}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">Aucun événement à venir</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Low Stock Alert */}
          {stats?.low_stock_products?.length > 0 && (
            <Card className="border-yellow-500/50 bg-yellow-500/5 print:hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-yellow-600 dark:text-yellow-500">
                  <AlertTriangle className="w-5 h-5" />
                  Stock faible
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {stats.low_stock_products.map((product) => (
                    <Badge
                      key={product.id}
                      variant="outline"
                      className="bg-yellow-500/10 text-yellow-600 dark:text-yellow-500 border-yellow-500/30"
                    >
                      {product.name}: {product.stock_quantity} restant(s)
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;